import { Resources } from 'constants/resources';

import React, { memo, useCallback } from 'react';

import clsx from 'clsx';
import { usePermissions } from 'hooks/usePermissions';
import {
  DatagridCell,
  DatagridClasses,
  RecordContextProvider,
  useRecordContext,
  useResourceContext,
} from 'react-admin';
import { Checkbox } from 'shared/mui/Checkbox/Checkbox';
import { TableCell, TableRow } from 'shared/mui/Table';
import { Typography } from 'shared/mui/Typography';
import { DatagridRowProps } from 'shared/react-admin/DatagridRows/DatagridRow/DatagridRow';
import { Permission } from 'types/embedded/permission';

type Props = Omit<DatagridRowProps, 'component' | 'sx'>;

const RequestsBankOpsListDatagridPureRow: React.ForwardRefExoticComponent<
  Omit<DatagridRowProps, 'ref' | 'sx'> & React.RefAttributes<HTMLTableRowElement>
> = React.forwardRef<HTMLTableRowElement, Props>((props, ref) => {
  const {
    children,
    className,
    hover = true,
    id,
    onToggleItem,
    selected = false,
    style,
    selectable = true,
    ...rest
  } = props;

  const record = useRecordContext(props);
  const resource = useResourceContext(props);
  const { hasResourcePermissions } = usePermissions();
  const handleToggleSelection = useCallback(
    (event: any) => {
      if (!selectable) {
        return;
      }
      if (id && onToggleItem) {
        onToggleItem(id, event);
      }
      event.stopPropagation();
    },
    [id, onToggleItem, selectable],
  );

  return (
    <RecordContextProvider value={record}>
      <TableRow
        className={clsx(className, {
          ['RaDatagrid-selectable']: selectable,
        })}
        hover={hover}
        key={id}
        ref={ref}
        style={style}
        {...rest}
      >
        <TableCell padding="checkbox" sx={{ padding: '13px 16px!important' }}>
          <Checkbox
            checked={selectable && selected}
            className="select-item RaDatagrid-checkbox"
            color="primary"
            disabled={
              !hasResourcePermissions(Resources.RequestBankOperations, [
                Permission.Delete,
                Permission.Full,
              ])
            }
            onClick={handleToggleSelection}
            sx={{ padding: 0 }}
          />
        </TableCell>
        {React.Children.map(children, (field: any, index) => (
          <DatagridCell
            className={clsx(`column-${field.props.source}`, 'RaDatagrid-rowCell')}
            key={index}
            record={record}
            {...{ field, resource }}
          />
        ))}
      </TableRow>
      {!!record?.related && (
        <RecordContextProvider value={record?.related}>
          <TableRow
            className={clsx(className)}
            hover={hover}
            key={id}
            ref={ref}
            {...rest}
            sx={{
              backgroundColor: 'tableHead',
            }}
          >
            <TableCell sx={{ padding: '12px 16px' }}>
              <Typography color="primary" textAlign="center">
                &#10149;
              </Typography>
            </TableCell>
            <TableCell className={clsx('column-originId', DatagridClasses.rowCell)} key={0}>
              {record?.related?.id}
            </TableCell>
            {React.Children.map(children, (field: any, index) => {
              if (field.props.source === 'originId' || !field.props?.label) {
                return <></>;
              }
              return (
                <DatagridCell
                  className={clsx(`column-${field.props.source}`, DatagridClasses.rowCell)}
                  key={index}
                  record={record?.related}
                  {...{ field, resource }}
                />
              );
            })}
            <TableCell key={13} />
          </TableRow>
        </RecordContextProvider>
      )}
    </RecordContextProvider>
  );
});

export const RequestsBankOpsListDatagridRow = memo(RequestsBankOpsListDatagridPureRow);

RequestsBankOpsListDatagridPureRow.displayName = 'BankOperationRow';
